import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardMedia,
  CardContent,
} from "@mui/material";
import ArchiveIcon from "@mui/icons-material/Archive";

const MenuItemsCompact = ({
  menuItems,
  ingredients,
  onArchive,
  onEdit,
  onEditOptions,
  onEditTags,
  onFetchAllergens,
}) => {
  console.log("menuItems:", menuItems);
  const findIngredient = (ingredientName) => {
    if (!ingredients || !Array.isArray(ingredients)) return null;

    const ingredient = ingredients.find(
      (ing) => ing.inventoryName === ingredientName
    );
    if (!ingredient) return null;

    const validInstances =
      ingredient.instances?.filter(
        (instance) =>
          instance.currentAmount > 0 &&
          new Date(instance.expiryDate) > new Date()
      ) || [];

    const totalCurrentAmount = validInstances.reduce(
      (sum, instance) => sum + instance.currentAmount,
      0
    );

    return {
      ...ingredient,
      totalCurrentAmount,
    };
  };

  const categorizedItems = menuItems.reduce((acc, item) => {
    let canMakeCount = Infinity;
    let lowStockIngredients = [];
    let anyIngredientMissing = false;

    item.menuIngredients?.forEach((ingredient) => {
      const detail = findIngredient(ingredient.ingredientName);
      if (!detail) {
        canMakeCount = 0;
        anyIngredientMissing = true;
      } else {
        const maxServings = Math.floor(
          detail.totalCurrentAmount / ingredient.amount
        );
        if (isNaN(maxServings) || maxServings <= 0) {
          canMakeCount = 0;
        } else {
          canMakeCount = Math.min(canMakeCount, maxServings);
          if (maxServings <= 10) {
            lowStockIngredients.push({
              ingredientName: ingredient.ingredientName,
              remainingServings: maxServings,
              requiredPerUnit: ingredient.amount,
            });
          }
        }
      }
    });

    const outOfStock = canMakeCount <= 0 || anyIngredientMissing;
    const isLowStock = !outOfStock && lowStockIngredients.length > 0;

    const category = outOfStock
      ? "Out of Stock"
      : isLowStock
      ? "Low Stock"
      : item.categoryName || "Uncategorized";

    if (!acc[category]) acc[category] = [];
    acc[category].push({ ...item, lowStockIngredients });

    return acc;
  }, {});

  const sortedCategories = Object.keys(categorizedItems).sort((a, b) => {
    const priority = { "Out of Stock": 0, "Low Stock": 1 };
    return (priority[a] ?? 2) - (priority[b] ?? 2) || a.localeCompare(b);
  });

  return (
    <Box>
      {sortedCategories.map((category, categoryIndex) => (
        <Box key={categoryIndex} sx={{ marginBottom: 4 }}>
          <Typography variant="h5" gutterBottom>
            {category}
          </Typography>

          {categorizedItems[category].map((item, index) => {
            const outOfStockIngredients = item.menuIngredients?.filter(
              (ingredient) => {
                const detail = findIngredient(ingredient.ingredientName);
                return !detail || detail.totalCurrentAmount < ingredient.amount;
              }
            );

            return (
              <Card
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  mb: 2,
                  backgroundColor:
                    outOfStockIngredients?.length > 0 ? "#ffe5e5" : "#f5f5f5",
                }}
              >
                <CardMedia
                  component="img"
                  image={item.menuImage || "/no-image.png"}
                  alt={item.menuName}
                  sx={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    borderRadius: "4px 0 0 4px",
                  }}
                />

                <CardContent sx={{ flex: 1, px: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {item.menuName}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Price: PHP {item.basePrice}
                  </Typography>

                  {item.menuTags?.some((tag) => tag.startsWith("spicy:")) &&
                    (() => {
                      const spicyTag = item.menuTags.find((tag) =>
                        tag.startsWith("spicy:")
                      );
                      const spicyName = spicyTag?.split("spicy: ")[1] || "";
                      const chiliCount =
                        spicyName === "mild"
                          ? 1
                          : spicyName === "pinoy"
                          ? 2
                          : spicyName === "thai"
                          ? 3
                          : 0;
                      return (
                        <Typography variant="body2" color="error">
                          Spicy: {spicyName} {"🌶️".repeat(chiliCount)}
                        </Typography>
                      );
                    })()}

                  <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                    {/* Low-stock ingredients (10 or fewer servings left) */}
                    {item.lowStockIngredients?.map((ingredient, i) => (
                      <li key={`low-${i}`}>
                        <span
                          onClick={() =>
                            window.open(
                              `/inventory?search=${encodeURIComponent(
                                ingredient.ingredientName
                              )}`,
                              "_blank"
                            )
                          }
                          style={{
                            color: "#ff8800", // orange
                            fontWeight: "bold",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          title="Go to Inventory"
                        >
                          {ingredient.ingredientName} –{" "}
                          {ingredient.remainingServings} unit
                          {ingredient.remainingServings !== 1 ? "s" : ""}{" "}
                          remaining
                        </span>
                      </li>
                    ))}

                    {/* Out of Stock ingredients (0 servings left or missing entirely) */}
                    {category === "Out of Stock" &&
                      item.menuIngredients?.map((ingredient, i) => {
                        const detail = findIngredient(
                          ingredient.ingredientName
                        );
                        const isMissing =
                          !detail ||
                          Math.floor(
                            detail.totalCurrentAmount / ingredient.amount
                          ) <= 0;

                        if (isMissing) {
                          return (
                            <li key={`missing-${i}`}>
                              <span
                                onClick={() =>
                                  window.open(
                                    `/inventory?search=${encodeURIComponent(
                                      ingredient.ingredientName
                                    )}`,
                                    "_blank"
                                  )
                                }
                                style={{
                                  color: "red",
                                  fontWeight: "bold",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                                title="Missing or Insufficient"
                              >
                                {ingredient.ingredientName} – Out of Stock
                              </span>
                            </li>
                          );
                        }

                        return null;
                      })}
                  </ul>

                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Button
                      onClick={() => onEdit(item)}
                      size="small"
                      variant="contained"
                      color="success"
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => onEditOptions(item)}
                      size="small"
                      variant="contained"
                    >
                      Options
                    </Button>
                    <Button
                      onClick={() => onEditTags(item)}
                      size="small"
                      variant="contained"
                      color="secondary"
                    >
                      Tags
                    </Button>
                    <Button
                      onClick={() => onFetchAllergens(item)}
                      size="small"
                      variant="contained"
                      color="warning"
                    >
                      Allergens
                    </Button>
                    <IconButton onClick={() => onArchive(item)} color="error">
                      <ArchiveIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default MenuItemsCompact;
